import styled from '@emotion/styled';
import React from 'react';
import { mqMin } from 'utils/responsive';
import { IAreaOption } from 'domain/entities/Buyer/Buyer';

interface FormAreaSelectProps {
    options: IAreaOption[];
    value: (string | undefined)[];
    onChange: (value: string[]) => void;
    placeholder?: string;
}

const Container = styled.div`
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: #f8fafc;
    padding: 16px 20px;
    max-width: 600px;
`;

const SelectAllContainer = styled.div`
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e0e0e0;
`;

const SelectAllCheckbox = styled.div`
    display: flex;
    align-items: center;
    font-weight: 700;
    color: var(--dark-blue);
    font-size: var(--font-large);
`;

const AreaGroup = styled.div`
    margin-bottom: 4px;
`;

const ParentCheckbox = styled.div`
    display: flex;
    align-items: center;
    font-weight: 700;
    color: var(--dark-blue);
    font-size: var(--font-large);
    margin-bottom: 4px;
    margin-top: 12px;
`;

const ChildCheckboxes = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 6px 8px;
    margin-left: 18px;
    margin-bottom: 8px;

    ${mqMin('NARROW')} {
        grid-template-columns: repeat(6, 1fr);
    }
`;

const ChildCheckbox = styled.div`
    display: flex;
    align-items: center;
    font-size: var(--font-medium);
    padding: 2px 0;
    transition: background 0.2s;
    border-radius: 4px;
    &:hover {
        background: #e6f0fa;
    }
`;

const Checkbox = styled.input`
    margin-right: 8px;
    cursor: pointer;
    appearance: auto;
`;

const Label = styled.label`
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
`;

const SelectAllLabel = styled.label`
    font-size: var(--font-medium);
`;

export const FormAreaSelect = React.forwardRef<HTMLDivElement, FormAreaSelectProps>(
    ({ options, value, onChange }, ref) => {
        const filteredValue = (value || []).filter((item): item is string => item !== undefined);

        const handleSelectAllClick = () => {
            const allChildCodes = options.flatMap((group) => group.children.map((child) => child.code));
            const selectedCount = allChildCodes.filter((code) => filteredValue.includes(code)).length;

            if (selectedCount === allChildCodes.length) {
                onChange([]);
            } else {
                onChange(allChildCodes);
            }
        };

        const isAllSelected = () => {
            const allChildCodes = options.flatMap((group) => group.children.map((child) => child.code));
            return allChildCodes.every((code) => filteredValue.includes(code));
        };

        const handleParentChange = (group: IAreaOption, checked: boolean) => {
            const childCodes = group.children.map((child) => child.code);
            let newValue: string[];

            if (checked) {
                // 親を選択した場合、子要素を全て追加
                const combined = [...filteredValue, ...childCodes];
                newValue = combined.filter((item, index) => combined.indexOf(item) === index);
            } else {
                // 親を選択解除した場合、子要素を全て削除
                newValue = filteredValue.filter((code) => !childCodes.includes(code));
            }

            onChange(newValue);
        };

        const handleChildChange = (childCode: string, checked: boolean) => {
            let newValue: string[];

            if (checked) {
                newValue = [...filteredValue, childCode];
            } else {
                newValue = filteredValue.filter((code) => code !== childCode);
            }

            onChange(newValue);
        };

        const isParentChecked = (group: IAreaOption) => {
            const childCodes = group.children.map((child) => child.code);
            return childCodes.every((code) => filteredValue.includes(code));
        };

        const isParentIndeterminate = (group: IAreaOption) => {
            const childCodes = group.children.map((child) => child.code);
            const selectedChildren = childCodes.filter((code) => filteredValue.includes(code));
            return selectedChildren.length > 0 && selectedChildren.length < childCodes.length;
        };

        return (
            <Container ref={ref}>
                <SelectAllContainer>
                    <SelectAllCheckbox>
                        <Checkbox
                            id="area-select-all-checkbox"
                            type="checkbox"
                            checked={isAllSelected()}
                            onChange={() => handleSelectAllClick()}
                        />
                        <SelectAllLabel htmlFor="area-select-all-checkbox">全て選択</SelectAllLabel>
                    </SelectAllCheckbox>
                </SelectAllContainer>
                {options.map((group, groupIdx) => {
                    const parentId = `area-parent-${groupIdx}`;
                    return (
                        <AreaGroup key={group.name}>
                            <ParentCheckbox>
                                <Checkbox
                                    id={parentId}
                                    type="checkbox"
                                    checked={isParentChecked(group)}
                                    ref={(input) => {
                                        if (input) {
                                            input.indeterminate = isParentIndeterminate(group);
                                        }
                                    }}
                                    onChange={(e) => handleParentChange(group, e.target.checked)}
                                />
                                <Label htmlFor={parentId}>{group.name}</Label>
                            </ParentCheckbox>
                            <ChildCheckboxes>
                                {group.children.map((child, childIdx) => {
                                    const childId = `area-child-${groupIdx}-${childIdx}`;
                                    return (
                                        <ChildCheckbox key={child.code}>
                                            <Checkbox
                                                id={childId}
                                                type="checkbox"
                                                checked={filteredValue.includes(child.code)}
                                                onChange={(e) => handleChildChange(child.code, e.target.checked)}
                                            />
                                            <Label htmlFor={childId}>{child.name}</Label>
                                        </ChildCheckbox>
                                    );
                                })}
                            </ChildCheckboxes>
                        </AreaGroup>
                    );
                })}
            </Container>
        );
    },
);
